/**
 * Courses API Client
 *
 * API Endpoints:
 * - GET /courses → CourseListResponse (user's accessible courses)
 * - GET /courses/catalog → All courses (discovery, no media fields)
 * - GET /courses/{id} → Course detail with videos (when accessible)
 * - GET /courses/play/{uuid} → Video playback data
 * - GET /course/{id}/trailer → Trailer URL
 * - GET /courses/bundles → All active bundles
 * - GET /courses/bundles/{id} → Single bundle detail
 * - POST /courses/{video:uuid}/progress → Save watch progress
 * - POST /courses/{course:uuid}/attachments → Upload a resource (owner only)
 * - PATCH /courses/attachments/{mediaUuid} → Rename / re-scope / reorder (owner only)
 * - DELETE /courses/attachments/{mediaUuid} → Remove a resource (owner only)
 */

import type {
  Course,
  CourseAttachment,
  CourseBundle,
  PaginatedResponse,
  UpdateCourseAttachmentPayload,
  UploadCourseAttachmentPayload,
  Video,
} from '../types';
import type { SeriesStructure } from '@/types/series';
import { captureError } from '@/shared/lib/errors/capture';
import { apiClient } from './base-client';
import axios from 'axios';

export interface CourseListFilters extends Record<string, unknown> {
  page?: number;
  per_page?: number;
  sort_by?: string;
}

export type CourseListResponse = PaginatedResponse<Course>;

export interface SaveProgressPayload {
  position: number;
  duration: number;
  last_action: 'pause' | 'seek' | 'abandon' | 'complete';
  playback_speed?: number;
}

const EMPTY_LIST_RESPONSE: CourseListResponse = {
  data: [],
  current_page: 1,
  last_page: 1,
  per_page: 0,
  total: 0,
  first_page_url: '',
  from: null,
  last_page_url: '',
  links: [],
  next_page_url: null,
  path: '',
  prev_page_url: null,
  to: null,
};

/** Extract a Course[] from the various response shapes the backend may return. */
function extractCourseList(data: unknown): CourseListResponse {
  if (!data || typeof data !== 'object') return EMPTY_LIST_RESPONSE;

  // { courses: [...] } wrapper
  if ('courses' in data) {
    const inner = (data as { courses: unknown }).courses;
    // Paginated: { courses: { data: [...], current_page, ... } }
    if (inner && typeof inner === 'object' && 'data' in inner && 'current_page' in inner) {
      return inner as CourseListResponse;
    }
    // Plain array: { courses: [...] }
    if (Array.isArray(inner)) {
      return { ...EMPTY_LIST_RESPONSE, data: inner, per_page: inner.length, total: inner.length };
    }
  }

  // Paginated at top level: { data: [...], current_page, ... }
  if ('data' in data && 'current_page' in data) {
    return data as CourseListResponse;
  }

  // Plain array at top level
  if (Array.isArray(data)) {
    return { ...EMPTY_LIST_RESPONSE, data, per_page: data.length, total: data.length };
  }

  return EMPTY_LIST_RESPONSE;
}

/** Extract the attachments array from various wrapper shapes. */
function extractAttachmentList(data: unknown): CourseAttachment[] {
  if (!data || typeof data !== 'object') return [];
  if ('attachments' in data && Array.isArray((data as { attachments: unknown }).attachments)) {
    return (data as { attachments: CourseAttachment[] }).attachments;
  }
  if ('data' in data && Array.isArray((data as { data: unknown }).data)) {
    return (data as { data: CourseAttachment[] }).data;
  }
  if (Array.isArray(data)) return data as CourseAttachment[];
  return [];
}

/** Extract a single Course from the detail response. */
function extractCourse(data: unknown): Course | null {
  if (!data || typeof data !== 'object') return null;
  if ('series' in data && (data as { series: unknown }).series) {
    return (data as { series: Course }).series;
  }
  if ('data' in data && (data as { data: unknown }).data) {
    return (data as { data: Course }).data;
  }
  if ('type' in data) return data as Course;
  return null;
}

export const coursesClient = {
  /**
   * List courses the user has access to (free + subscribed).
   * Returns up to 30 courses filtered by whereHasAuthSubscription scope.
   */
  list: async (filters?: CourseListFilters): Promise<CourseListResponse> => {
    try {
      const data = await apiClient.get<unknown>('/courses', {
        params: filters as Record<string, unknown>,
      });
      return extractCourseList(data);
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'list' });
      return EMPTY_LIST_RESPONSE;
    }
  },

  /**
   * List ALL courses (discovery catalog). Includes courses the user does NOT have access to.
   * Response has no media fields (thumbnails absent) — cards show gradient fallback.
   */
  catalog: async (): Promise<Course[]> => {
    try {
      const data = await apiClient.get<unknown>('/courses/catalog');
      if (!data || typeof data !== 'object') return [];
      if ('courses' in data && Array.isArray((data as { courses: unknown }).courses)) {
        return (data as { courses: Course[] }).courses;
      }
      if (Array.isArray(data)) return data as Course[];
      return [];
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'catalog' });
      return [];
    }
  },

  /**
   * Get course detail by ID.
   * Returns full metadata always; videos[] is populated only when user_has_access is true.
   */
  getDetail: async (id: string | number): Promise<Course | null> => {
    try {
      const data = await apiClient.get<unknown>(`/courses/${id}`);
      return extractCourse(data);
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'getDetail' });
      return null;
    }
  },

  /**
   * Module tree. Sub-gated; `null` on any failure so callers fall back to
   * the flat lesson list (guests on the public course page hit 401).
   */
  getStructure: async (id: string | number): Promise<SeriesStructure | null> => {
    try {
      return await apiClient.get<SeriesStructure>(`/courses/${id}/with-modules`);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status !== 401 && status !== 403 && status !== 404) {
        captureError(error, { component: 'CoursesClient', action: 'getStructure' });
      }
      return null;
    }
  },

  /**
   * Get course videos by course ID.
   * Extracts the videos array from the detail endpoint response.
   */
  getVideos: async (id: string | number): Promise<Video[]> => {
    try {
      const data = await apiClient.get<unknown>(`/courses/${id}`);
      const course = extractCourse(data);
      return course?.videos ?? [];
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'getVideos' });
      return [];
    }
  },

  /**
   * Play a course video by UUID.
   * Returns empty-like response (not HTTP error) when access is denied.
   */
  playVideo: async (uuid: string): Promise<Video> => {
    const data = await apiClient.get<unknown>(`/courses/play/${uuid}`);

    if (!data || typeof data !== 'object') {
      throw new Error(`Invalid response for video ${uuid}`);
    }

    // Wrapped in "video" key
    if (
      'video' in data &&
      data.video &&
      typeof data.video === 'object' &&
      !Array.isArray(data.video)
    ) {
      return data.video as Video;
    }
    // Wrapped in "data" key
    if ('data' in data && (data as { data: unknown }).data) {
      return (data as { data: Video }).data;
    }
    // Direct video resource shape
    if ('uuid' in data || 'title' in data) {
      return data as Video;
    }

    throw new Error(`Video ${uuid} not found or access denied`);
  },

  /**
   * Get course trailer URL.
   */
  getTrailer: async (id: string | number): Promise<{ url: string }> => {
    return apiClient.get<{ url: string }>(`/course/${id}/trailer`);
  },

  /**
   * List all active course bundles with their courses.
   */
  getBundles: async (): Promise<CourseBundle[]> => {
    try {
      const data = await apiClient.get<unknown>('/courses/bundles');

      if (!data || typeof data !== 'object') return [];

      // { bundles: [...] } or { data: { bundles: [...] } }
      if ('bundles' in data && Array.isArray((data as { bundles: unknown }).bundles)) {
        return (data as { bundles: CourseBundle[] }).bundles;
      }
      if ('data' in data) {
        const inner = (data as { data: unknown }).data;
        if (inner && typeof inner === 'object' && 'bundles' in inner) {
          return (inner as { bundles: CourseBundle[] }).bundles;
        }
      }
      if (Array.isArray(data)) return data as CourseBundle[];

      return [];
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'getBundles' });
      return [];
    }
  },

  /**
   * Get a single course bundle by ID.
   * Returns 404 if bundle is not found or inactive.
   */
  getBundleDetail: async (id: string | number): Promise<CourseBundle | null> => {
    try {
      const data = await apiClient.get<unknown>(`/courses/bundles/${id}`);

      if (!data || typeof data !== 'object') return null;

      // Wrapped in "bundle" key
      if ('bundle' in data && (data as { bundle: unknown }).bundle) {
        return (data as { bundle: CourseBundle }).bundle;
      }
      // Nested inside "data" wrapper
      if ('data' in data) {
        const inner = (data as { data: unknown }).data;
        if (inner && typeof inner === 'object' && 'bundle' in inner) {
          return (inner as { bundle: CourseBundle }).bundle;
        }
        if (inner && typeof inner === 'object' && 'id' in inner) {
          return inner as CourseBundle;
        }
      }
      if ('id' in data && 'whop_plan_id' in data) return data as CourseBundle;

      return null;
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'getBundleDetail' });
      return null;
    }
  },

  /**
   * List course resource attachments by course UUID.
   * Route binds `{course:uuid}` to the Series.uuid column — never send numeric series.id.
   * Returns [] on any error. 403/404 are part of the contract (silent empty)
   * and never reach Sentry; only 5xx/429 are reported.
   */
  listAttachments: async (courseUuid: string): Promise<CourseAttachment[]> => {
    try {
      const data = await apiClient.get<unknown>(`/courses/${courseUuid}/attachments`);
      return extractAttachmentList(data);
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status !== undefined && status >= 500) {
        captureError(error, { component: 'CoursesClient', action: 'listAttachments' });
      } else if (status === 429) {
        captureError(error, { component: 'CoursesClient', action: 'listAttachments' });
      }
      return [];
    }
  },

  /**
   * Upload a course resource. Owner-only server-side.
   *
   * Unlike the read methods above, the write methods THROW: the caller needs
   * the 422 body to tell a creator whether they hit the file cap, a bad MIME,
   * or an oversized file. Swallowing into a fallback would strand the UI.
   */
  uploadAttachment: async (
    courseUuid: string,
    payload: UploadCourseAttachmentPayload
  ): Promise<CourseAttachment> => {
    const form = new FormData();
    form.append('file', payload.file);
    if (payload.title !== undefined) form.append('title', payload.title);
    if (payload.description !== undefined) form.append('description', payload.description);
    if (payload.video_id !== undefined) form.append('video_id', String(payload.video_id));

    const data = await apiClient.post<{ attachment: CourseAttachment }>(
      `/courses/${courseUuid}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data.attachment;
  },

  /**
   * Patch a resource. The route is media-scoped — it carries no course uuid, so
   * callers must keep the course uuid themselves for cache invalidation.
   * Sending `video_id: null` dissociates the lesson.
   */
  updateAttachment: async (
    mediaUuid: string,
    payload: UpdateCourseAttachmentPayload
  ): Promise<CourseAttachment> => {
    const data = await apiClient.patch<{ attachment: CourseAttachment }>(
      `/courses/attachments/${mediaUuid}`,
      payload
    );
    return data.attachment;
  },

  /** Delete a resource. BE removes the file from the storage disk in the same call. */
  deleteAttachment: async (mediaUuid: string): Promise<void> => {
    await apiClient.delete<{ deleted: boolean }>(`/courses/attachments/${mediaUuid}`);
  },

  /**
   * Save video watch progress. Fire-and-forget — errors are captured but not thrown.
   */
  saveProgress: async (videoUuid: string, payload: SaveProgressPayload): Promise<void> => {
    try {
      await apiClient.post(`/courses/${videoUuid}/progress`, payload);
    } catch (error) {
      captureError(error, { component: 'CoursesClient', action: 'saveProgress' });
    }
  },
};
